import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Send, CheckCircle, Loader2, User, Mail, Phone, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTIES = [
  { 
    id: 'osceola', 
    name: 'Osceola County', 
    director: 'Wendy Font',
    title: 'Community Resource Director',
    areas: 'Kissimmee, St. Cloud, Poinciana, Celebration'
  },
  { 
    id: 'orange', 
    name: 'Orange County', 
    director: 'Wendy Font',
    title: 'Community Resource Director',
    areas: 'Orlando, Winter Park, Apopka, Winter Garden'
  },
  { 
    id: 'polk', 
    name: 'Polk County', 
    director: 'Julio Guerra',
    title: 'Community Care Director',
    areas: 'Lakeland, Winter Haven, Haines City, Davenport'
  }
];

export default function CountyContactModal({ isOpen, onClose }) {
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCounty) {
      toast.error('Please select your county');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/county-inquiries/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          county: selectedCounty.name
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        setAssignedTo(data.assigned_to);
        toast.success('Message sent!');
      } else {
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCounty(null);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setSubmitted(false);
    setAssignedTo('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="county-contact-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-amber-700 p-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <MapPin size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-serif text-white">Contact Your County Director</h3>
                  <p className="text-sm text-white/80">Get help from your local representative</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-green-600" size={32} />
                </div>
                <h4 className="font-serif text-xl mb-2 text-charcoal">Message Sent!</h4>
                <p className="text-charcoal/70 mb-4">
                  Your message has been sent to <strong>{assignedTo}</strong>. 
                  They will respond to you soon.
                </p>
                <button
                  onClick={handleClose}
                  className="px-6 py-2 rounded-full bg-amber-100 text-amber-700 font-medium hover:bg-amber-200 transition-colors"
                >
                  Close
                </button>
              </motion.div>
            ) : (
              <>
                {/* County Selection */}
                {!selectedCounty ? (
                  <div>
                    <p className="text-charcoal/70 mb-4 text-center">
                      Select your county to connect with the right director:
                    </p>
                    <div className="space-y-3">
                      {COUNTIES.map((county) => (
                        <button
                          key={county.id}
                          onClick={() => setSelectedCounty(county)}
                          className="w-full p-4 rounded-xl border-2 border-gold/20 hover:border-amber-400 hover:bg-amber-50 transition-all text-left group"
                          data-testid={`select-county-${county.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-charcoal group-hover:text-amber-700">
                                {county.name}
                              </h4>
                              <p className="text-xs text-charcoal/50">{county.areas}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-amber-700">{county.director}</p>
                              <p className="text-xs text-charcoal/50">{county.title}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Contact Form */
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Selected County Banner */}
                    <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-700 uppercase tracking-wide">Contacting</p>
                        <p className="font-medium text-charcoal">{selectedCounty.director}</p>
                        <p className="text-xs text-charcoal/60">{selectedCounty.name}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedCounty(null)}
                        className="text-xs text-amber-700 hover:underline"
                      >
                        Change
                      </button>
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-charcoal">Your Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                        <input
                          type="text"
                          required
                          maxLength={100}
                          pattern="[A-Za-z\s\-']{2,100}"
                          title="Please enter a valid name (letters, spaces, hyphens only)"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value.slice(0, 100)})}
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                          placeholder="Your full name"
                          data-testid="inquiry-name"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-charcoal">Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                        <input
                          type="email"
                          required
                          maxLength={254}
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value.slice(0, 254)})}
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                          placeholder="your@email.com"
                          data-testid="inquiry-email"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-charcoal">Phone (optional)</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                        <input
                          type="tel"
                          maxLength={20}
                          pattern="[\d\s\-\(\)\+]{7,20}"
                          title="Please enter a valid phone number"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/[^\d\s\-\(\)\+]/g, '').slice(0, 20)})}
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                          placeholder="(xxx) xxx-xxxx"
                          data-testid="inquiry-phone"
                        />
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-charcoal">Subject *</label>
                      <input
                        type="text"
                        required
                        maxLength={200}
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value.slice(0, 200)})}
                        className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        placeholder="What is your question about?"
                        data-testid="inquiry-subject"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-charcoal">Message *</label>
                      <div className="relative">
                        <textarea
                          required
                          rows={4}
                          maxLength={2000}
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value.slice(0, 2000)})}
                          className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-amber-400/50 resize-none"
                          placeholder="How can we help you?"
                          data-testid="inquiry-message"
                        />
                        <span className="absolute bottom-2 right-2 text-xs text-charcoal/40">{formData.message.length}/2000</span>
                      </div>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="submit-inquiry"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Export a button component to trigger the modal
export function CountyContactButton({ className = '' }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-all hover:scale-105 ${className}`}
        data-testid="county-contact-btn"
      >
        <MapPin size={18} />
        Contact Your County Director
      </button>
      <CountyContactModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}
